import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useTypedTranslation } from '@app/hooks';
import { getLoaderBtn, getModalSwitch } from '@app/store/actions';
import { ModalActionsComponent, PasslyTextField } from '@app/components';
import { onPressEnterModal, genericErrorToast } from '@app/helpers';
import { vaultsServiceNew } from '@app/services';
import {IVaultPasswordPolicy, TranslationsTypes} from '@app/models';
import { PasslyAutoComplete } from '@app/componentsV2';
import { IVaultItem } from '@app/pages/passwordManager/interfaces';
import {getDefaultPolicy, getDisplayNameError} from '@app/pages/passwordManager/helpers';

export interface IReceivedData {
    handleRefresh: Function;
    passwordPolicies: IVaultPasswordPolicy[];
    allVaults: IVaultItem[];
}

export const AddVaultDialog = ({ handleRefresh, passwordPolicies, allVaults }: IReceivedData) => {
    const dispatch = useDispatch();
    const { translation } = useTypedTranslation();

    const [displayName, setDisplayName] = useState('');
    const [description, setDescription] = useState('');
    const [defaultFolder, setDefaultFolder] = useState('');
    const [displayNameError, setDisplayNameError] = useState('');
    const [folderNameError, setFolderNameError] = useState('');
    const [passwordPolicy, setPasswordPolicy] = useState<string>('');
    const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);

    useEffect(() => {
        setIsSubmitDisabled(!displayName || !!displayNameError || !defaultFolder || !!folderNameError);
    }, [displayName, defaultFolder, displayNameError, folderNameError]);

    useEffect(() => {
        setPasswordPolicy(getDefaultPolicy(passwordPolicies));
    }, [passwordPolicies]);

    const onFormChange = useCallback(
        (e: any) => {
            const { name, value } = e.target;

            if (name === 'displayName') {
                const error: TranslationsTypes = getDisplayNameError(value, allVaults);

                setDisplayName(value);
                setDisplayNameError(error ? translation(error) : '');
            } else if (name === 'description') {
                setDescription(value);
            } else if (name === 'defaultFolder') {
                setDefaultFolder(value);
                setFolderNameError(value ? '' : translation('REQUIRED_FIELD'));
            } else if (name === 'passwordPolicy') {
                setPasswordPolicy(value.id);
            }
        },
        [allVaults]
    );

    const handleSaveBtn = useCallback(() => {
        dispatch(getLoaderBtn(true));

        const jsonObject = {
            displayName: displayName,
            description: description,
            passwordPolicyId: passwordPolicy,
            folders: [{ displayName: defaultFolder }]
        };

        vaultsServiceNew.addVault(jsonObject, handleError, handleSuccess);
    }, [displayName, passwordPolicy, defaultFolder]);

    const handleError = useCallback((response: any) => {
        dispatch(getLoaderBtn(false));
        genericErrorToast(response);
    }, []);

    const handleSuccess = useCallback(() => {
        dispatch(getLoaderBtn(false));
        dispatch(getModalSwitch(false));
        handleRefresh(true);
    }, [handleRefresh]);

    const handleCancel = useCallback(() => {
        dispatch(getLoaderBtn(false));
        dispatch(getModalSwitch(false));
    }, []);

    return (
        <>
        <div className='modal__form' tabIndex={0} onKeyPress={onPressEnterModal(handleSaveBtn, !isSubmitDisabled)}>
            <>
                <div className='modal__body--row--space'>
                    <PasslyTextField
                        name='displayName'
                        label={translation('VAULT_NAME')}
                        value={displayName}
                        onChange={onFormChange}
                        id='displayName-input'
                        inputProps={{ maxLength: 255 }}
                        autoFocus
                        required
                        error={!!displayNameError}
                        helperText={displayNameError}
                    />
                </div>
{/*                <div className='modal__body--row--space'>
                    <PasslyTextField
                        fullWidth
                        multiline
                        rows={4}
                        label={translation('DESCRIPTION')}
                        id='description-input'
                        name='description'
                        onChange={onFormChange}
                        type='text'
                        value={description}
                        inputProps={{ maxLength: 500 }}
                    />
                </div>*/}
                <div className='modal__body--row--space'>
                    <PasslyTextField
                        name='defaultFolder'
                        label={translation('DEFAULT_FOLDER')}
                        value={defaultFolder}
                        onChange={onFormChange}
                        id='defaultFolder-input'
                        inputProps={{ maxLength: 255 }}
                        required
                        error={!!folderNameError}
                        helperText={folderNameError}
                    />
                </div>
                <div className='modal__body--row--space'>
                    <PasslyAutoComplete
                        className='passwordPolicy'
                        items={passwordPolicies}
                        onChange={onFormChange}
                        id='passwordPolicy-input'
                        name='passwordPolicy'
                        label={translation('VAULT_PASSWORD_POLICY')}
                        value={passwordPolicy}
                        valueField='id'
                        field='displayName'
                        dropdown={true}
                        appendTo={document.body}
                    />
                </div>
            </>
        </div>
            <ModalActionsComponent
                primaryBtn={translation('ADD_VAULT')}
                secondaryBtn={translation('CANCEL')}
                onClickPrimary={handleSaveBtn}
                onClickSecondary={handleCancel}
                disabled={isSubmitDisabled}
                id='addVault'
            />
        </>
    );
};
